"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";
import Badge from "../Badge";

interface UsageDataPoint {
  day: string;
  value: number;
  target: number;
}

const WaterUsageTracker: React.FC = () => {
  const [activePeriod, setActivePeriod] = useState<'day' | 'week' | 'month'>('week');
  
  // Sample data for the usage chart
  const weeklyData: UsageDataPoint[] = [
    { day: "Mon", value: 28, target: 30 },
    { day: "Tue", value: 32, target: 30 },
    { day: "Wed", value: 25, target: 30 },
    { day: "Thu", value: 29, target: 30 },
    { day: "Fri", value: 22, target: 30 },
    { day: "Sat", value: 18, target: 30 },
    { day: "Sun", value: 27, target: 30 },
  ];
  
  // Calculate the max value for scaling the chart
  const maxValue = Math.max(...weeklyData.map(item => Math.max(item.value, item.target))) * 1.2;
  
  return (
    <main className="pb-96 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-2xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex flex-col justify-center w-full">
                  <h1 className="w-full">
                    Water Usage Tracker
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-8 w-full">
            <div className="px-4 w-full">
              {/* Period selector */}
              <div className="flex gap-2 justify-center w-full bg-gray-100 p-1 rounded-xl">
                <button 
                  onClick={() => setActivePeriod('day')} 
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex-1 ${activePeriod === 'day' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                >
                  Day
                </button>
                <button 
                  onClick={() => setActivePeriod('week')} 
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex-1 ${activePeriod === 'week' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                >
                  Week
                </button>
                <button 
                  onClick={() => setActivePeriod('month')} 
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex-1 ${activePeriod === 'month' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                >
                  Month
                </button>
              </div>

              {/* Current usage summary */}
              <div className="mt-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center">
                  <h2 className="text-base font-semibold text-gray-900">Current Week Usage</h2>
                  <Badge variant="success">-7% vs Target</Badge>
                </div>
                <div className="mt-4 flex justify-between items-baseline">
                  <div>
                    <p className="text-3xl font-semibold text-gray-900">181L</p>
                    <p className="text-sm text-gray-500 mt-1">Daily Average</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-500">Target: </span>
                    <Badge variant="outline">195L</Badge>
                  </div>
                </div>
              </div>

              {/* Usage chart */}
              <div className="mt-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
                <h2 className="text-base font-semibold text-gray-900">Usage Breakdown</h2>
                
                <div className="mt-6 h-60 relative">
                  {/* Y axis labels */}
                  <div className="absolute left-0 top-0 bottom-0 w-10 flex flex-col justify-between text-xs text-gray-500">
                    <span>{Math.round(maxValue)}L</span>
                    <span>{Math.round(maxValue * 0.75)}L</span>
                    <span>{Math.round(maxValue * 0.5)}L</span>
                    <span>{Math.round(maxValue * 0.25)}L</span>
                    <span>0L</span>
                  </div>

                  {/* Chart grid lines */}
                  <div className="absolute left-10 right-0 top-0 bottom-0">
                    <div className="absolute top-0 left-0 right-0 border-t border-gray-100"></div>
                    <div className="absolute top-1/4 left-0 right-0 border-t border-gray-100"></div>
                    <div className="absolute top-2/4 left-0 right-0 border-t border-gray-100"></div>
                    <div className="absolute top-3/4 left-0 right-0 border-t border-gray-100"></div>
                    <div className="absolute bottom-0 left-0 right-0 border-t border-gray-100"></div>
                  </div>

                  {/* Chart bars */}
                  <div className="absolute left-12 right-0 top-0 bottom-6 flex justify-between items-end">
                    {weeklyData.map((item, index) => (
                      <div key={index} className="flex flex-col items-center w-10">
                        <div className="relative h-full w-full flex items-end mb-1">
                          {/* Target line */}
                          <div 
                            className="absolute w-10 border-t-2 border-dashed border-blue-300" 
                            style={{ bottom: `${(item.target / maxValue) * 100}%`, left: '-5px' }}
                          ></div>
                          {/* Usage bar */}
                          <div 
                            className="w-6 bg-blue-500 rounded-t-md mx-auto"
                            style={{ height: `${(item.value / maxValue) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">{item.day}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-4 flex justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500"></div>
                    <span>Usage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0 border-t-2 border-dashed border-blue-300"></div>
                    <span>Target</span>
                  </div>
                </div>
              </div>

              {/* Water-saving tips */}
              <div className="mt-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
                <h2 className="text-base font-semibold text-gray-900">Water Saving Tips</h2>
                
                <div className="mt-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h3 className="font-medium text-blue-700">Running below target!</h3>
                    <p className="mt-1 text-sm text-gray-700">You're using 7% less water than your target. Keep up the good work!</p>
                  </div>
                  
                  <div className="mt-3 flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 5V10M10 10V15M10 10H15M10 10H5" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Tip of the day</h3>
                      <p className="mt-1 text-sm text-gray-700">Use a water displacement device in your toilet tank to reduce water used per flush.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
    </main>
  );
};

export default WaterUsageTracker;
